import { Bugfender, LogLevel } from '@bugfender/sdk';
import { isEqual, toLower, toUpper } from 'lodash-es';

import {
  COUNTRY_PHONE_CODES,
  COUNTRY_PHONE_CODES_ARRAY,
} from '@/constants/countryCodes';
import { CURRENCY_USD } from '@/constants/currency';
import { IDENTITY_DOCUMENTS, TitleText } from '@/constants/user';

import i18n from '@/i18n/i18n';
import { getTranslatedMessage } from '@/i18n/utils';

import { AccountTypeDetails } from '@/types/api/account';
import { PEPAnswer, PEPQuestion, QuestionCode } from '@/types/api/pep';
import {
  AccountDepositInfo,
  UpdateDepositInfoRequestPayload,
} from '@/types/api/user';
import { Tenant } from '@/types/common';
import { Option } from '@/types/components/common';
import {
  BugfenderTagType,
  Document,
  DocumentSection,
  DocumentStatus,
} from '@/types/document';
import { Language } from '@/types/i18n';
import { CountryISO, Territory } from '@/types/places';
import {
  AccountFormData,
  ApplicationType,
  ClientType,
  EmploymentStatus,
  HolderType,
  MaritalStatus,
  OperationType,
  Profession,
  User,
  UserAccountType,
  UserStatus,
} from '@/types/user';

import { i18nCountryTranslator } from '@/utils/countryTranslations';
import { formatDate, formatDateValue } from '@/utils/string';

import { isTestUser } from './api';
import { isCaymanTenant, isGuyanaTenant } from './tenant';

export function isNationalUser(user: User, tenantIsoCode = 'unknown-iso-code') {
  return [user.citizenship, user.citizenship2]
    .filter(i => !!i)
    .map(i => toUpper(i))
    .includes(toUpper(tenantIsoCode));
}

export function getUserCurrency(
  user: User,
  options: {
    accountTypeById: Record<string, AccountTypeDetails>;
    defaultCurrency: string;
  },
) {
  Bugfender.sendLog({
    tag: BugfenderTagType.MORE_INFO,
    level: LogLevel.Info,
    text: JSON.stringify({
      action: 'getUserCurrency',
      userAccountTypes: user?.accountTypes,
      accountTypeById: options.accountTypeById,
    }),
  });

  if (!user?.accountTypes?.length) {
    return '';
  }

  const { accountTypeById, defaultCurrency } = options;
  const firstAccount = user.accountTypes[0];
  return accountTypeById[firstAccount.id]?.feeCurrency || defaultCurrency;
}

export function getUserMonthlyDepositCurrency(
  user: User | undefined,
  options: {
    accountTypeById: Record<string, AccountTypeDetails>;
    defaultCurrency: string;
  },
) {
  if (!user?.accountTypes?.length) {
    return '';
  }
  const currentAccountTypes = user.accountTypes.filter(
    account => account.operationId === user.operationId,
  );
  if (!currentAccountTypes.length) {
    return '';
  }
  const { accountTypeById, defaultCurrency } = options;

  const foundNationalCurrencyAccount = currentAccountTypes.find(account => {
    const accountDetails = accountTypeById[account.id];
    return accountDetails?.feeCurrency === defaultCurrency;
  });

  if (foundNationalCurrencyAccount) {
    return defaultCurrency;
  }

  const foundUSDAccount = currentAccountTypes.find(account => {
    const accountDetails = accountTypeById[account.id];
    return accountDetails?.feeCurrency === CURRENCY_USD;
  });

  if (foundUSDAccount) {
    return CURRENCY_USD;
  }

  const firstAccount = currentAccountTypes[0];
  return accountTypeById[firstAccount.id]?.feeCurrency || defaultCurrency;
}

const US_INDICIA_COUNTRY_SELECTORS = [
  (user: User) => user.citizenship,
  (user: User) => user.citizenship2,
  (user: User) => user.countryOfBirth,
  (user: User) => user.residenceCountry,
  (user: User) => user.addressDetailed?.country,
  (user: User) => user.country,
  (user: User) => parseTaxResidences(user.taxResidences),
  (user: User) =>
    Object.entries(user.tin ?? {})
      .filter(([, taxNumber]) => Boolean(taxNumber))
      .map(([countryCode]) => countryCode),
] as const;

function isUSCountryCode(countryCode?: string): boolean {
  if (!countryCode) return false;
  const normalized = toUpper(countryCode);
  return normalized === CountryISO.US;
}

function collectUSIndiciaCountryCodes(user: User): string[] {
  return US_INDICIA_COUNTRY_SELECTORS.flatMap(selector => {
    const value = selector(user);
    return Array.isArray(value) ? value : value ? [value] : [];
  });
}

export function hasUSIndicia(user?: User): boolean {
  if (!user) return false;
  return (
    !!user.usIndicia || collectUSIndiciaCountryCodes(user).some(isUSCountryCode)
  );
}

const FOREIGN_INDICIA_EXEMPT_COUNTRY_CODES = new Set<string>([
  'AIA',
  CountryISO.Australia,
  CountryISO.Bermuda,
  CountryISO.BVI,
  CountryISO.Cayman,
  CountryISO.Japan,
  'KNA',
]);

const MAX_FOREIGN_INDICIA_COUNTRIES = 5;

export enum ForeignIndiciaSource {
  Citizenship = 'citizenship',
  Citizenship2 = 'citizenship2',
  CountryOfBirth = 'countryOfBirth',
  ResidenceCountry = 'residenceCountry',
  AddressCountry = 'addressCountry',
  Country = 'country',
  EmployerCountry = 'employerCountry',
}

export type ForeignIndiciaCountryDetail = {
  countryCode: string;
  sources: ForeignIndiciaSource[];
};

type ForeignIndiciaField = {
  source: ForeignIndiciaSource;
  getCountryCode: (user: User) => string | undefined;
};

const FOREIGN_INDICIA_FIELDS: ForeignIndiciaField[] = [
  {
    source: ForeignIndiciaSource.Citizenship,
    getCountryCode: user => user.citizenship,
  },
  {
    source: ForeignIndiciaSource.Citizenship2,
    getCountryCode: user => user.citizenship2,
  },
  {
    source: ForeignIndiciaSource.CountryOfBirth,
    getCountryCode: user => user.countryOfBirth,
  },
  {
    source: ForeignIndiciaSource.ResidenceCountry,
    getCountryCode: user => user.residenceCountry,
  },
  {
    source: ForeignIndiciaSource.AddressCountry,
    getCountryCode: user => user.addressDetailed?.country,
  },
  {
    source: ForeignIndiciaSource.Country,
    getCountryCode: user => user.country,
  },
  {
    source: ForeignIndiciaSource.EmployerCountry,
    getCountryCode: user => {
      const employmentStatus = user.employmentDetailed?.employmentStatus;

      if (
        employmentStatus === EmploymentStatus.SelfEmployed ||
        employmentStatus === EmploymentStatus.Employed
      ) {
        return user.employmentDetailed?.employerCountry;
      }
    },
  },
];

function isForeignIndiciaCountry(
  countryCode: string | undefined,
  tenantCountryCode: string,
): boolean {
  if (!countryCode) return false;

  const normalized = toUpper(countryCode);

  return (
    normalized !== toUpper(tenantCountryCode) &&
    !isUSCountryCode(normalized) &&
    !FOREIGN_INDICIA_EXEMPT_COUNTRY_CODES.has(normalized)
  );
}

export function getForeignIndiciaCountries(
  user?: User,
  tenantCountryCode?: string,
): ForeignIndiciaCountryDetail[] {
  if (!user || !tenantCountryCode) return [];

  const countryDetails = new Map<string, ForeignIndiciaSource[]>();
  for (const { source, getCountryCode } of FOREIGN_INDICIA_FIELDS) {
    const countryCode = getCountryCode(user);
    if (!isForeignIndiciaCountry(countryCode, tenantCountryCode)) {
      continue;
    }

    const normalizedCountryCode = toUpper(countryCode!);
    const sources = countryDetails.get(normalizedCountryCode) ?? [];

    if (!sources.includes(source)) {
      sources.push(source);
    }

    countryDetails.set(normalizedCountryCode, sources);
  }

  return Array.from(countryDetails.entries(), ([countryCode, sources]) => ({
    countryCode,
    sources,
  })).slice(0, MAX_FOREIGN_INDICIA_COUNTRIES);
}

export function hasForeignCountryIndicia(
  user?: User,
  tenantCountryCode?: string,
): boolean {
  return getForeignIndiciaCountries(user, tenantCountryCode).length > 0;
}

export function isUSCitizen(user?: User) {
  if (!user?.citizenship && !user?.citizenship2) return false;

  return [user.citizenship, user.citizenship2]
    .filter(i => !!i)
    .map(i => i?.toUpperCase())
    .includes(CountryISO.US);
}

export function isExistingCustomer(user?: User) {
  return toLower(user?.type) === toLower(ClientType.Existing);
}

export function isIdentityCheckComplete(user?: User) {
  return IDENTITY_DOCUMENTS.every(type =>
    user?.documents?.some(document => document.type === type),
  );
}

export function isRemediationCustomer(
  user?: Pick<User, 'type' | 'clientType'>,
) {
  return (
    toLower(user?.type) === toLower(OperationType.Remediation) ||
    toLower(user?.clientType) === OperationType.Remediation.toLowerCase()
  );
}

export function isMobileAdoption(type: ClientType) {
  return isEqual(toLower(type), ClientType.MobileAdoption);
}

export function isUSTaxResidence(user?: User) {
  if (!user?.taxResidences) return false;

  return parseTaxResidences(user?.taxResidences)
    .map(countryCode => countryCode.toUpperCase())
    .includes(CountryISO.US);
}

export function parseTaxResidences(taxResidences?: string): string[] {
  return (
    taxResidences
      ?.split(',')
      .map(code => code.trim())
      .filter(Boolean) ?? []
  );
}

export function isCaymanUser(user?: User) {
  return isCaymanTenant(user?.tenant);
}

export function isGuyanaUser(user?: User) {
  return isGuyanaTenant(user?.tenant);
}

export function isArchivedUser(user?: User) {
  return user?.status === UserStatus.Archived;
}

export function isRejectedUser(user?: User) {
  return user?.status === UserStatus.Rejected;
}

export function isSurinameUser(user?: User) {
  return user?.tenant === Tenant.Suriname;
}

export function isTrinidadOrTobagoUser(user?: User) {
  return user?.tenant === Tenant.Trinidad;
}

export function isGrenadaUser(user?: User) {
  return user?.tenant === Tenant.Grenada;
}

export function isBVIUser(user?: User) {
  return user?.tenant === Tenant.BVI;
}

export function hasUSFactor(user?: User) {
  return isUSCitizen(user) || isUSTaxResidence(user) || !!user?.usIndicia;
}

export function isNotUSAndLocalTaxResidence(user?: User) {
  if (!user?.taxResidences) return false;
  const taxResidences = parseTaxResidences(user.taxResidences).map(code =>
    code.toUpperCase(),
  );

  const excludedResidences = new Set(
    [CountryISO.US, user.territoryCode]
      .filter(Boolean)
      .map(code => code.toUpperCase()),
  );

  return taxResidences.every(code => !excludedResidences.has(code));
}

export function isEmploymentInfoCollected(user: User): boolean {
  const status = user.employmentDetailed?.employmentStatus;
  const employmentDetailed = user.employmentDetailed ?? {};

  if (!status) return false;

  if (status === EmploymentStatus.Employed) {
    return Boolean(employmentDetailed?.employerCountry);
  }

  if (status === EmploymentStatus.SelfEmployed) {
    if (isCaymanUser(user)) {
      return !!(
        employmentDetailed?.employerName &&
        employmentDetailed?.businessCategoryCode &&
        employmentDetailed?.occupationCode
      );
    }
    if (user.riskSectorFlag === false) {
      return (
        Boolean(employmentDetailed?.employerCountry) &&
        !!employmentDetailed?.employmentDesc
      );
    }
    return !!employmentDetailed?.employmentDesc;
  }

  return !!(
    user.riskSectorFlag ||
    employmentDetailed?.employmentDesc ||
    employmentDetailed?.riskSectorCategory
  );
}

export function getCitizenships(user: User, language: Language): string {
  if (!user.citizenship) return '';

  const firstCitizenship =
    i18nCountryTranslator.getNationalityName(user.citizenship, language) ?? '';

  if (user.tenant === Tenant.Suriname || user.tenant === Tenant.Barbados) {
    return firstCitizenship;
  }

  if (user.citizenship2Has && user.citizenship2) {
    const secondCitizenship = i18nCountryTranslator.getNationalityName(
      user.citizenship2,
      language,
    );
    return `${firstCitizenship}, ${secondCitizenship}`;
  }

  return firstCitizenship;
}

export function getDisplayCountryName(
  countryCode: string | undefined,
  userLanguage: Language = Language.English,
) {
  if (!countryCode) return '';

  return i18nCountryTranslator.getCountryName(countryCode, userLanguage) ?? '';
}

export function getPlaceOfBirth(user: User) {
  const parts = [
    user.cityOfBirth?.toUpperCase(),
    getDisplayCountryName(
      user.countryOfBirth,
      (user.language || Language.English) as Language,
    )?.toUpperCase(),
  ].filter(Boolean);

  return parts.join(', ');
}

/**
 * @param user - The user object
 * @param isMonthlyDeposit - Whether to get the monthly deposit or income
 * @returns The monthly income or deposit with the currency
 */
export function getMonthlyIncome(user: User, isMonthlyDeposit?: boolean) {
  const query = isMonthlyDeposit ? 'monthlyDeposit' : 'monthlyIncome';
  if (!user[query]) return '';
  const currency = user?.[`${query}Currency`]
    ? ` ${user?.[`${query}Currency`]}`
    : '';
  return `${user[query]}${currency}`;
}

export function getPEPAnswer(user: User, pepQuestions: PEPQuestion[]) {
  const pepConnectionQuestionId = pepQuestions?.find(q =>
    q.code.includes(QuestionCode.Connection),
  )?.id;

  const pepRelativesQuestionId = pepQuestions?.find(q =>
    q.code.includes(QuestionCode.Relatives),
  )?.id;

  const pepConnectionAnswer = user.lastAnswer?.answers.find(
    answer => answer.questionId === pepConnectionQuestionId,
  );

  const pepRelativesAnswer = user.lastAnswer?.answers.find(
    answer => answer.questionId === pepRelativesQuestionId,
  );

  return { pepConnectionAnswer, pepRelativesAnswer };
}

export function getPEPDate(
  startDate: string | undefined,
  endDate: string | undefined,
  dateFormat: string,
) {
  if (!startDate) return '';

  return `${formatDate(startDate, dateFormat)} - ${endDate ? formatDate(endDate, dateFormat) : 'now'}`;
}

export function getPEPDetails(
  pepConnectionAnswer: PEPAnswer | undefined,
  pepRelativesAnswer: PEPAnswer | undefined,
  dateFormat: string,
) {
  const pepConnectionPosition = pepConnectionAnswer?.position
    ? `(e): ${pepConnectionAnswer?.position}`
    : '';
  const pepConnectionDate = getPEPDate(
    pepConnectionAnswer?.startDate,
    pepConnectionAnswer?.endDate,
    dateFormat,
  );

  const pepRelativesName = pepRelativesAnswer?.fullName
    ? `(f): ${pepRelativesAnswer?.fullName}`
    : '';
  const pepRelativesRelationShip = pepRelativesAnswer?.relationship;
  const pepRelativesPosition = pepRelativesAnswer?.position;
  const pepRelativesDate = getPEPDate(
    pepRelativesAnswer?.startDate,
    pepRelativesAnswer?.endDate,
    dateFormat,
  );

  const pepConnectionDetails = [pepConnectionPosition, pepConnectionDate]
    .filter(Boolean)
    .join(', ');

  const pepRelativesDetails = [
    pepRelativesName,
    pepRelativesPosition,
    pepRelativesDate,
    pepRelativesRelationShip,
  ]
    .filter(Boolean)
    .join(', ');

  const pepDetails = [pepConnectionDetails, pepRelativesDetails]
    .filter(Boolean)
    .join(' | ');

  return pepDetails;
}

export function getSuggestedUsernames(
  username: string,
  birthday: string = '',
  phoneNumber: string = '',
) {
  const REQUIRE_USERNAME_LENGTH = 10;
  const shortUsername = username.slice(0, REQUIRE_USERNAME_LENGTH - 1);
  const formattedBirthday = formatDateValue(birthday, 'ddMMyyyy');
  const numberString = formattedBirthday + phoneNumber;
  const lengthDiff = REQUIRE_USERNAME_LENGTH - shortUsername.length;
  const possibleSequences = numberString.length - lengthDiff + 1;
  const suggestions = new Set<string>();

  for (let i = 0; i < possibleSequences; i++) {
    const numberSequence = numberString.slice(i);
    const suggestion = (shortUsername + numberSequence).slice(
      0,
      REQUIRE_USERNAME_LENGTH,
    );
    suggestions.add(suggestion);
  }

  return Array.from(suggestions);
}

export function getAppointmentBranchName(
  user?: User,
  territoryByCountryCode?: Record<string, Territory>,
): string {
  if (!user?.visitingBranchId || !user.territoryCode) return '';

  const branchId = user.visitingBranchId;
  const territoryCode = user.territoryCode;
  const branchName =
    territoryByCountryCode?.[territoryCode]?.branches?.find(
      branch => branch.id === branchId,
    )?.branchName || '';

  return branchName;
}

export function extractLocalPhoneNumber(fullPhoneNumber: string) {
  if (!fullPhoneNumber) return { tenant: '', phoneNumber: '' };
  let tenant: Tenant = '' as Tenant;
  let countryCode: string = '';
  const phoneNumber = fullPhoneNumber.replace(/[\s\-+()]/g, ''); // Remove all known special characters
  // A full number can have 2 formats: with number 1 at start or not.
  // We need to find the tenant and country code from the full number.
  void Object.entries(COUNTRY_PHONE_CODES_ARRAY).find(([key, codes]) => {
    codes.some(code => {
      // Check if full number start with our supported country code or not.
      if (phoneNumber.startsWith(code)) {
        tenant = key as Tenant;
        countryCode = code;
        return true;
      }
      return false;
    });
    // If we found the tenant and country code, we can stop the loop.
    if (tenant && countryCode) {
      return true;
    }
    return false;
  })?.[0];
  if (tenant && countryCode) {
    return {
      tenant: COUNTRY_PHONE_CODES[tenant as Tenant],
      phoneNumber: phoneNumber.slice(countryCode.length),
    };
  }
  return { tenant: '', phoneNumber };
}

export function getOccupationName({
  occupations,
  directorOccupations,
  user,
}: {
  occupations: Option[];
  directorOccupations: Option[];
  user?: User;
}) {
  const position = user?.employmentDetailed?.employmentDesc;
  const occupationCode = user?.employmentDetailed?.occupationCode;
  if (position === Profession.Director && !isCaymanUser(user)) {
    return (
      directorOccupations.find(
        occupation => occupation.value === occupationCode,
      )?.text || ''
    );
  }
  return (
    occupations.find(occupation => occupation.value === occupationCode)?.text ||
    ''
  );
}

export function hasNonUSIssuedPassport(user: User) {
  return user.documents.some(
    doc =>
      doc.type === 'Identity' &&
      doc.docType === 'Passport' &&
      doc.issueCountry !== CountryISO.US,
  );
}

export function isCountryOfBirthUS(user?: User) {
  return (user?.countryOfBirth || '').toUpperCase() === CountryISO.US;
}

export function isTrinidadOrTobagoCitizen(user?: User) {
  if (!user?.citizenship && !user?.citizenship2) return false;
  return [user.citizenship, user.citizenship2]
    .filter(Boolean)
    .map(code => (code as string).toUpperCase())
    .includes(CountryISO.Trinidad);
}

export function hasNoUsTin(user?: User) {
  if (!user?.tin) return true;
  return !(CountryISO.US in user.tin);
}

export function isUSRenouncementAllowed(user?: User) {
  if (!user) return false;
  return (
    isCountryOfBirthUS(user) &&
    !isUSCitizen(user) &&
    hasNonUSIssuedPassport(user) &&
    hasNoUsTin(user)
  );
}

export function getMaritalStatusOptions() {
  return [
    {
      value: MaritalStatus.Single,
      text: getTranslatedMessage('maritalStatus.single'),
      i18nKey: 'maritalStatus.single',
    },
    {
      value: MaritalStatus.Married,
      text: getTranslatedMessage('maritalStatus.married'),
      i18nKey: 'maritalStatus.married',
    },
    {
      value: MaritalStatus.CommonLaw,
      text: getTranslatedMessage('maritalStatus.commonLaw'),
      i18nKey: 'maritalStatus.commonLaw',
    },
    {
      value: MaritalStatus.Divorced,
      text: getTranslatedMessage('maritalStatus.divorced'),
      i18nKey: 'maritalStatus.divorced',
    },
    {
      value: MaritalStatus.Widow,
      text: getTranslatedMessage('maritalStatus.widow'),
      i18nKey: 'maritalStatus.widow',
    },
  ];
}

export function getNormalizedTaxResidences(
  taxResidences?: string | string[],
): string[] {
  if (!taxResidences || !taxResidences.length) return [];

  let values: string[] = [];
  if (Array.isArray(taxResidences)) {
    values = [...taxResidences];
  } else {
    const taxString = taxResidences;
    values = taxString.split(',');
  }

  return values.map(code => code.trim()).filter(Boolean);
}

export function shouldShowTinForCountry(
  user: User | undefined,
  taxCountries: string[],
  countryCode: CountryISO,
): boolean {
  return [user?.citizenship, ...taxCountries].includes(countryCode);
}

export function getOtherTaxCountries(
  selectedTaxCountries: string[],
  excludeCountries: (CountryISO | string)[],
): string[] {
  return selectedTaxCountries.filter(code => !excludeCountries.includes(code));
}

export function getOtherTins(
  tins: Record<string, string> | undefined,
  exclude: (CountryISO | string)[],
): Record<string, string> {
  return Object.fromEntries(
    Object.entries(tins || {}).filter(([key]) => !exclude.includes(key)),
  );
}

export function isExpectedMonthlyDepositCollected(user: User): boolean {
  return (
    user.accountTypes?.some(account => {
      const caymanAccount = account as unknown as AccountDepositInfo;
      return (
        caymanAccount.estimatedMonthlyDepositAmount !== undefined ||
        caymanAccount.estimatedMonthlyDepositCount !== undefined ||
        caymanAccount.estimatedMonthlyCashDepositAmount !== undefined
      );
    }) ?? false
  );
}

export function getStampDuty(message: string): string {
  const match = /(?<=<b>)(.*?)(?=<\/b>)/.exec(message);
  return match ? match[0] : '';
}

export type EkycDocumentsStatus = {
  isIDChecked: boolean;
  isSelfieChecked: boolean;
  isLivenessVideoRecorded: boolean;
};

export const checkEkycDocumentsStatus = (
  documents: Document[] = [],
): EkycDocumentsStatus => {
  const statuses = {
    isIDChecked: false,
    isSelfieChecked: false,
    isLivenessVideoRecorded: false,
  };

  if (!documents.length) return statuses;

  const activeDocuments = documents.filter(
    item => item.status !== DocumentStatus.Rejected,
  );

  for (const item of activeDocuments) {
    if (item.type === DocumentSection.Identity && item.uri) {
      statuses.isIDChecked = true;
    } else if (item.type === DocumentSection.Selfie && item.uri) {
      statuses.isSelfieChecked = true;
    } else if (item.type === DocumentSection.IDVideo && item.uri) {
      statuses.isLivenessVideoRecorded = true;
    }
  }

  return statuses;
};

export const checkUserEkycStatus = (user?: User) =>
  checkEkycDocumentsStatus(user?.documents);

export const isEkycStatusComplete = (
  status: EkycDocumentsStatus,
  isRecordDisabled = false,
) =>
  status.isIDChecked &&
  status.isSelfieChecked &&
  (isRecordDisabled || status.isLivenessVideoRecorded);

export function getDepositInfoPayload(
  bankingAccounts: UserAccountType[],
  formDataPayload: AccountFormData[],
) {
  const depositInfoArray: UpdateDepositInfoRequestPayload = [];

  bankingAccounts.forEach((account, index) => {
    const accountData = formDataPayload[index];

    depositInfoArray.push({
      accountTypeId: account.id,
      estimatedMonthlyDepositAmount: accountData.monthlyDepositAmount
        ? Number.parseFloat(accountData.monthlyDepositAmount)
        : undefined,
      estimatedMonthlyDepositCount: accountData.monthlyDepositsCount
        ? Number.parseInt(accountData.monthlyDepositsCount, 10)
        : undefined,
      estimatedMonthlyCashDepositAmount: accountData.monthlyCashDepositAmount
        ? Number.parseFloat(accountData.monthlyCashDepositAmount)
        : undefined,
      willSendReceiveFromOthers: accountData.sendReceiveMoney,
      willUseInternationalWireTransfers: accountData.internationalWireTransfers,
      isBusinessPurpose: accountData.isBusinessPurpose,
      accountNo: account.accountNo,
    });
  });
  return depositInfoArray;
}

export function isBusinessPurposeQuestionEnabled(user: User): boolean {
  if (isCaymanUser(user)) {
    return false;
  }

  return (
    [UserStatus.Draft, UserStatus.InReview, UserStatus.Archived].includes(
      user.status,
    ) &&
    !(user.accountTypes || []).some(item =>
      Object.prototype.hasOwnProperty.call(item, 'isBusinessPurpose'),
    )
  );
}

export function hasOnlyNationalIdDocs(documents: Document[]): boolean {
  const identityDocuments = documents.filter(
    doc => doc.type === DocumentSection.Identity,
  );

  if (identityDocuments.length === 0) {
    return false;
  }

  return identityDocuments.every(doc => doc.docType === 'ID');
}

export function isGuyanaExcWithIdOnly(user?: User): boolean {
  if (!user) return false;

  if (!isGuyanaUser(user) || !isExistingCustomer(user)) {
    return false;
  }

  return hasOnlyNationalIdDocs(user.documents);
}

function hasTaxInNonUSAndNonAccountCountry(user: User): boolean {
  const taxResidences = parseTaxResidences(user.taxResidences);
  const accountCountryCode = user.territoryCode?.toUpperCase();

  const isTaxInNonUSAndNonAccountCountry = taxResidences.some(countryCode => {
    const upperCode = countryCode.toUpperCase();
    return upperCode !== CountryISO.US && upperCode !== accountCountryCode;
  });

  return !!isTaxInNonUSAndNonAccountCountry;
}

function hasMissingTIN(user: User): boolean {
  const taxResidences = parseTaxResidences(user.taxResidences);
  const accountCountryCode = user.territoryCode?.toUpperCase();

  const isMissingTIN = taxResidences.some(countryCode => {
    const upperCode = countryCode.toUpperCase();
    if (upperCode === CountryISO.US || upperCode === accountCountryCode) {
      return false;
    }
    return !user.tin?.[upperCode];
  });

  return !!isMissingTIN;
}

function paysTaxInExemptCountry(user: User): boolean {
  const taxResidences = parseTaxResidences(user.taxResidences);
  const exemptCountries = [
    CountryISO.Australia,
    CountryISO.Bermuda,
    CountryISO.BVI,
    CountryISO.Cayman,
    CountryISO.Japan,
  ];
  const exemptCountryCodes = new Set(
    exemptCountries.map(code => code.toUpperCase()),
  );
  const isPayingTaxInExemptCountry = taxResidences.some(countryCode =>
    exemptCountryCodes.has(countryCode.toUpperCase()),
  );

  return !!isPayingTaxInExemptCountry;
}

function shouldShowBranchVisitForNonUSCustomerMissingTIN(user?: User): boolean {
  if (!user || hasUSFactor(user)) {
    return false;
  }

  if (!user.taxResidences) {
    return false;
  }

  if (!hasTaxInNonUSAndNonAccountCountry(user)) {
    return false;
  }

  if (!hasMissingTIN(user)) {
    return false;
  }

  return !paysTaxInExemptCountry(user);
}

function meetsBranchVisitAdditionalCriteria(user: User): boolean {
  const isHighRisk = user.isHighRisk === true;
  const isMediumRisk = user.isHighRisk !== true;
  const isPreApproved = user.status === UserStatus.PreApproved;
  const isUSMissingTIN = hasUSFactor(user) && hasNoUsTin(user);
  const isNonUSMissingTIN =
    shouldShowBranchVisitForNonUSCustomerMissingTIN(user);

  return (
    isHighRisk ||
    isMediumRisk ||
    isPreApproved ||
    isUSMissingTIN ||
    isNonUSMissingTIN
  );
}

function shouldShowBranchVisitForSelfEmployed(
  user: User,
  hasBusinessPurpose: boolean,
): boolean {
  if (!isExistingCustomer(user)) {
    const requiresBusinessAccount =
      hasBusinessPurpose ||
      (user.employmentDetailed?.riskSectorFlag === false &&
        !hasBusinessPurpose &&
        !user.isHighRisk);

    if (requiresBusinessAccount) {
      return meetsBranchVisitAdditionalCriteria(user);
    }

    return false;
  }
  return hasBusinessPurpose;
}

function shouldShowBranchVisitForEmployedOrUnemployed(
  user: User,
  hasBusinessPurpose: boolean,
): boolean {
  if (!isExistingCustomer(user) && hasBusinessPurpose) {
    return meetsBranchVisitAdditionalCriteria(user);
  }

  return hasBusinessPurpose;
}

function shouldShowBranchVisitForBusinessAccountRequirement(
  user?: User,
): boolean {
  if (!user) return false;

  const employmentStatus = user.employmentDetailed?.employmentStatus;
  const hasBusinessPurpose = Boolean(
    user.accountTypes?.some(account => account.isBusinessPurpose),
  );

  if (employmentStatus === EmploymentStatus.SelfEmployed) {
    return shouldShowBranchVisitForSelfEmployed(user, hasBusinessPurpose);
  }

  if (
    employmentStatus &&
    [EmploymentStatus.Employed, EmploymentStatus.Unemployed].includes(
      employmentStatus,
    )
  ) {
    return shouldShowBranchVisitForEmployedOrUnemployed(
      user,
      hasBusinessPurpose,
    );
  }

  return false;
}

export function shouldShowExcBranchVisitPopup(user?: User): boolean {
  if (!user || !isExistingCustomer(user)) {
    return false;
  }

  if (user.isHighRisk === true) {
    return true;
  }

  if (hasUSFactor(user) && hasNoUsTin(user)) {
    return true;
  }

  if (shouldShowBranchVisitForNonUSCustomerMissingTIN(user)) {
    return true;
  }

  if (shouldShowBranchVisitForBusinessAccountRequirement(user)) {
    return true;
  }

  return false;
}

export function shouldShowBranchVisitPopup(user?: User): boolean {
  if (!user || isExistingCustomer(user)) {
    return false;
  }

  if (!user.infoAccuracyConfirmed) {
    return false;
  }

  if (user.isHighRisk === true) {
    return true;
  }

  if (hasUSFactor(user) && hasNoUsTin(user)) {
    return true;
  }

  if (shouldShowBranchVisitForNonUSCustomerMissingTIN(user)) {
    return true;
  }

  if (shouldShowBranchVisitForBusinessAccountRequirement(user)) {
    return true;
  }

  return false;
}

export function getFullName({
  firstName,
  lastName,
  middleName,
}: {
  firstName: string;
  lastName: string;
  middleName?: string;
}): string {
  return Array.from([firstName, middleName, lastName])
    .filter((part): part is string => (part ? part.length > 0 : false))
    .join(' ');
}

export const isMinorUser = (user?: User): boolean => {
  return (
    user?.applicationType === ApplicationType.JOINT &&
    user?.holderType === HolderType.JointMinor
  );
};

export const isAdultUser = (user?: User): boolean => {
  return (
    user?.applicationType === ApplicationType.JOINT &&
    user?.holderType === HolderType.JointAdult
  );
};

export const isRightStartUser = (user?: User): boolean => {
  return (
    user?.applicationType === ApplicationType.JOINT &&
    (user?.holderType === HolderType.JointAdult ||
      user?.holderType === HolderType.JointMinor)
  );
};

export const isIndividualUser = (user?: User): boolean => {
  return user?.holderType === HolderType.Individual;
};

export function skipICBCheckingPage(user?: User) {
  return isTestUser(user) || isMinorUser(user);
}

export function getUserTitle(titleValue: number): string {
  if (Number.isNaN(titleValue)) return '';

  const text = TitleText[String(titleValue)];
  return text ? toLower(text.replace(/\./g, '')) : '';
}

export function getProfessionName(
  professionName: string,
  language: Language,
): string {
  if (!professionName) return '';

  return i18n.t(`${professionName}`, {
    lng: language,
    defaultValue: professionName,
  });
}

export function calculateMinorAge(birthDate: string | undefined): number {
  if (!birthDate) return 0;
  const birth = new Date(birthDate);
  const today = new Date();
  let age = today.getFullYear() - birth.getFullYear();
  const m = today.getMonth() - birth.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) age--;
  return age;
}
